import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, FolderOpen, ShoppingCart, IndianRupee, TrendingUp, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { sellerAPI } from '@/lib/api';

export default function DashboardHome({ profile }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await sellerAPI.getStatistics();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Products', 
      value: stats?.products_count || 0, 
      icon: Package, 
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    { 
      title: 'Categories', 
      value: stats?.categories_count || 0, 
      icon: FolderOpen, 
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    },
    { 
      title: 'Total Orders', 
      value: stats?.total_orders || 0, 
      icon: ShoppingCart, 
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    { 
      title: 'Revenue', 
      value: `₹${(stats?.total_revenue || 0).toLocaleString()}`, 
      icon: IndianRupee, 
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">
            Welcome back{profile?.name ? `, ${profile.name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's what's happening with your shop today.
          </p>
        </div>
        {profile && (
          <Link to={`/shop/${profile.shop_slug}`} target="_blank">
            <Button variant="outline" className="gap-2">
              <ExternalLink className="h-4 w-4" />
              View My Shop
            </Button>
          </Link>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <div className={`h-8 w-8 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Orders Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Pending Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-heading font-bold text-primary">
              {stats?.pending_orders || 0}
            </div>
            <p className="text-sm text-muted-foreground mt-1">Awaiting your action</p>
            <Link to="/seller/dashboard/orders">
              <Button className="mt-4 w-full" variant="outline" size="sm">
                View Orders
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Accepted Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-heading font-bold text-secondary">
              {stats?.accepted_orders || 0}
            </div>
            <p className="text-sm text-muted-foreground mt-1">Successfully processed</p>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-heading">This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-heading font-bold">
              {stats?.monthly_orders || 0}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              ₹{(stats?.monthly_revenue || 0).toLocaleString()} revenue
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="font-heading">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Link to="/seller/dashboard/products">
            <Button className="gap-2">
              <Package className="h-4 w-4" /> Add Product
            </Button>
          </Link>
          <Link to="/seller/dashboard/categories">
            <Button variant="outline" className="gap-2">
              <FolderOpen className="h-4 w-4" /> Manage Categories
            </Button>
          </Link>
          <Link to="/seller/dashboard/statistics">
            <Button variant="outline" className="gap-2">
              <TrendingUp className="h-4 w-4" /> View Statistics
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
